import { useEffect, useRef, useState } from 'react';
import { tr } from './lib/i18n.ts';
import { errorMessage } from './lib/api.ts';
import { useSession } from './lib/session.ts';
import { useMediaQuery } from './lib/useMediaQuery.ts';
import { AuthScreen } from './AuthScreen.tsx';
import { Desktop } from './desktop/Desktop.tsx';
import { MobileShell } from './desktop/MobileShell.tsx';
import { AppFallback } from './desktop/AppBoundary.tsx';
import { useInfoBarPrefsSync } from './infobar/prefsStore.ts';
import { useWallpaperSync } from './wallpapers/store.ts';
import { useDesktop } from './desktop/store.ts';

export function App() {
  const session = useSession();
  const userId = session.data?.id ?? null;
  const shell = useShellMode();
  useInfoBarPrefsSync(userId);
  useWallpaperSync(userId);

  // A different account (or none) must never inherit the previous account's windows.
  const lastUser = useRef(userId);
  useEffect(() => {
    if (lastUser.current !== userId) useDesktop.getState().reset();
    lastUser.current = userId;
  }, [userId]);

  if (session.isPending) return <AppFallback />;
  if (session.isError) {
    return (
      <div className="app-state app-state-page" role="alert">
        <p>{errorMessage(session.error)}</p>
        <button type="button" className="btn" onClick={() => session.refetch()}>
          {tr.common.retry}
        </button>
      </div>
    );
  }
  if (!session.data) return <AuthScreen />;
  return shell === 'compact' ? <MobileShell user={session.data} /> : <Desktop user={session.data} />;
}

/**
 * Phone or desktop shell. Switching unmounts every open app, so while any app holds unsaved work the
 * current shell stays put (e.g. an iPad rotating across the breakpoint) until that work is saved.
 */
function useShellMode(): 'compact' | 'desk' {
  const compact = useMediaQuery('(max-width: 767px)');
  const unsaved = useDesktop((state) => Object.values(state.unsaved).some(Boolean));
  const [mode, setMode] = useState<'compact' | 'desk'>(compact ? 'compact' : 'desk');
  const wanted = compact ? 'compact' : 'desk';
  useEffect(() => {
    if (wanted !== mode && !unsaved) setMode(wanted);
  }, [wanted, mode, unsaved]);
  return mode;
}
